/**
 * PageNameMapper - Maps Page_Id to Sidebar Display Names
 * 
 * This utility maps database Page IDs to the user-friendly names shown in the sidebar.
 * When a new page is added to the database, add its mapping here to display the correct sidebar name.
 * 
 * If a page ID is not found in this mapping, it will fall back to the database name (page_NameEn).
 */

// Map of Page_Id to Sidebar Display Name
// This mapping is based on the sidebar labels defined in SidebarContent.js
export const PAGE_ID_TO_SIDEBAR_NAME = {
  // User Management & Settings
  1: 'User Management',           // /GmsUser
  82: 'Role Management',           // /GmsRoleManagement
  9: 'Dashboard',                  // /Dashboard, /GmsAdminDashboard

  // Human Resources
  2: 'Employee List',               // /GmsEmployee
  83: 'Add Employee',               // /GmsAddEmployee
  3: 'Department',                 // /GmsDepartment
  4: 'Designation',                // /GmsDesignation
  5: 'Group',                      // /GmsCompany
  6: 'Branch',                     // /GmsBranch
  7: 'Company',                    // /GmsBusiness
  10: 'Leave Request',             // /GmsEmployeeLeave
  8: 'Leave Types',                // /GmsLeaveType
  11: 'Update Leave Balances',     // /GmsEmployeeLeaveCredit
  27: 'Annual Leave Credit Processing', // /GmsAnnualLeaveCredit
  81: 'Daily Attendance',          // /GmsDailyAttendance
  12: 'Salary Advance',            // /GmsSalaryAdvance
  26: 'Salary Revision',           // /GmsSalaryRevision
  15: 'Payroll Processing',        // /GmsPayrollProcessing
  23: 'Expiring Documents',        // /GmsExpiringDocuments

  // Reports - HR
  16: 'Salary Advance Transaction Report', // /GmsSalaryAdvanceTransactionReport
  17: 'Payroll History Report',    // /GmsPayrollHistoryReport
  18: 'Payroll Details History Report', // /GmsPayrollDetailsHistory Report
  47: 'Employee Report',           // /GmsEmployeeReport

  // New Reports
  105: 'Daybook Report',
  106: 'Source Based Report',
  107: 'Service Monthly Report',
  46: 'Booking Reports',           // /GmsBookingReport
  50: 'Stock Report',              // /GmsStockReport
  54: 'Daily Summary Report',      // /GmsDailySummary
  71: 'Source Based Customer Count and Revenue', // /GmsSourceBasedReport
  93: 'Customer Segregation Report', // /GmsCustomerSegregationReport
  95: 'Campaign Report',           // /GmsCampaignReport

  // Reports - Employee Performance
  21: 'Day Book',                  // /GmsDaybook
  89: 'Add Day Book Entry',        // /GmsAddDaybook
  22: 'Sales Book',                // /GmsSalesbook

  // Customers & Bookings
  29: 'New Customer',              // /GmsCustomer
  72: 'Customer List',             // /GmsCustomerList
  41: 'Booking Calendar',          // /GmsSpaCalendar
  73: 'New Booking',               // /GmsAddBooking
  32: 'Booking List',              // /GmsBooking
  42: 'Membership Plans',          // /GmsMembership
  100: 'Visit Notes (SOAP)',       // /GmsSoapEntry
  101: 'SOAP History',            // /GmsSoapHistory

  // Sales & Payments
  38: 'Add Invoice',                // /GmsBilling
  74: 'Add Receipt',               // /GmsAddReceipt
  58: 'Receipts List',             // /GmsCustomerReceipts
  76: 'Add Cash Deposit',          // /GmsAddCashDeposit
  61: 'Cash Deposits List',        // /GmsCashDeposit
  67: 'Petty Cash In',             // /GmsPettyCashReceipts
  66: 'Other Payee',               // /GmsOtherPayee
  68: 'Petty Cash Out',            // /GmsPettyCashPayments
  79: 'Petty Cash Report',         // /GmsPettyCashReport
  59: 'X Report',                  // /GmsDailyClosing
  60: 'Z Report',                  // /GmsZReport

  // Services & Packages
  36: 'Service Catalogue',         // /GmsSpaService
  34: 'Categories',                // /GmsSpaCategory
  35: 'Subcategories',            // /GmsSpaSubCategory
  37: 'Packages',                  // /GmsSpaPackages
  40: 'Series Packages',           // /GmsSpaPackageSeries

  // Inventory & Suppliers
  20: 'Products',                 // /GmsProductManagement
  80: 'Add Product',               // /GmsAddProduct
  30: 'Rooms',                     // /GmsRoom
  31: 'Equipment',                 // /GmsEquipment
  51: 'Stock Transfer',            // /GmsStockTransfer
  52: 'Receive Stock',             // /GmsReceiveStockTransfer
  48: 'Supplier Management',       // /GmsSupplier
  57: 'Supplier Category',         // /GmsSupplierCategory
  56: 'Purchase Order',            // /GmsPurchaseOrder
  77: 'Add Purchase Order',        // /GmsAddPurchaseOrder
  49: 'Purchases',                 // /GmsPurchase
  78: 'Add Purchase',              // /GmsAddPurchase
  53: 'Stock Lock Management',     // /GmsStockLockManagement
  84: 'Price Catalog',             // /GmsPriceCatalog

  // Settings - Business Setup
  33: 'Tax',                       // /GmsTax
  64: 'Source',          // /GmsSource
  94: 'Campaign Management', // /GmsCampaign

  // Settings - Finance Setup
  62: 'Banks',                     // /GmsBank
  63: 'Bank Accounts',             // /GmsBankAccount
  65: 'Cost Heads',                // /GmsCostHead
  90: 'Gift Cards',                // /GmsGiftCard

  // Others
  25: 'Service Management',         // /GmsService
  19: 'Activity Management',       // /GmsActivityManagement
  28: 'Activities',                // /GmsActivityUpdated
  43: 'Notifications',             // /GmsNotifications

  // Dashboards
  44: 'Spa Employee Dashboard',     // /GmsSpaEmployeeDashboard
  45: 'Spa Manager & Finance Dashboard', // /GmsSpaManagerFinanceDashboard

  // Restaurant
  85: 'Tables',                     // /GmsRestaurantTable
  86: 'Restaurant Booking List',    // /GmsRestaurantBookingList
  87: 'Loyalty Points',             // /GmsLoyaltyPoints

  // Deprecated/Inactive pages (keeping for reference, but may not appear in sidebar)
  13: 'Leave Calculator',          // /GmsLeaveCalculator (inactive)
  14: 'Absent Days',               // /GmsAbsentDays (inactive)
  24: 'Group Management',          // /GmsGroup (inactive)
  55: 'Payment Request',           // (inactive)
  69: 'Petty Cash Receipts List',  // (deprecated)
  70: 'Petty Cash Payments List',  // (deprecated)
  91: 'Billed Services Report',    // /GmsBilledServicesReport
  92: 'Message Templates',        // /GmsMessageTemplate
  96: 'Dashboard',
  97: 'WhatsApp Leads',
  98: 'Maintenance Tickets Report',
  99: 'Quick Invoice',
  102: 'Employee Separation',
  103: 'Separation List',
  104: 'Account Head',
  105: 'Account Sub-Head',
  106: 'ASH Mapping',
  108: 'Daily Booking Summary', // /GmsDailyBookingSummary
  109: 'Ledger Posting',
};

/**
 * Get the sidebar display name for a given Page ID
 * @param {number} pageId - The Page_Id from the database
 * @param {string} fallbackName - The database page name to use as fallback (page_NameEn)
 * @returns {string} The sidebar display name or fallback name
 */
export const getSidebarName = (pageId, fallbackName = '') => {
  return PAGE_ID_TO_SIDEBAR_NAME[pageId] || fallbackName || `Page ${pageId}`;
};

/**
 * Get all page IDs that have sidebar names mapped
 * @returns {number[]} Array of page IDs
 */
export const getMappedPageIds = () => {
  return Object.keys(PAGE_ID_TO_SIDEBAR_NAME).map(id => parseInt(id));
};

/**
 * Check if a page ID has a sidebar name mapping
 * @param {number} pageId - The Page_Id to check
 * @returns {boolean} True if mapping exists
 */
export const hasSidebarMapping = (pageId) => {
  return pageId in PAGE_ID_TO_SIDEBAR_NAME;
};
